/* eslint-disable */
/**
 * Browser only socket connection / presence.
 * v2.1
 *
 * Ties into tracking logic. The tracking call will be ignored by the
 * server if trackerId is not provided (or is null).
 */

import _each from 'lodash/each'
import _find from 'lodash/find'
import _findIndex from 'lodash/findIndex'
import _has from 'lodash/has'
import _map from 'lodash/map'

import { Socket as PhoenixSocket } from 'phoenix'
import Vue from 'vue'

export default ({ uri, token, fingerprint, ipAddress, projectId, debug = false }) => {
  const socketUri = uri || 'wss://api.edgewiserealty.com/socket'
  const origin = window ? window.location.origin : null

  const params = {
    token,
    agent: 'browser',
    ip_address: ipAddress || null,
    origin,
    fingerprint: fingerprint || null,
    project_id: projectId || null
  }
  const transport = new PhoenixSocket(socketUri, { params })

  /**
   * Wrap socket in Vue instance so we get reactive data and methods.
   * Especially useful for presence connections.
   */
  const socket = new Vue({
    data: {
      debug,
      token,
      transport,
      connectionStart: null,
      connectionEnd: null,
      connected: false,
      channels: {}
    },

    /**
     */
    computed: {
      lobbyPresence() {
        const hasLobbyConnections = _has(this, 'channels[lobby].connections')
        return hasLobbyConnections ? this.channels.lobby.connections : []
      },
      lobbyDisconnections() {
        const hasLobbyDisconnections = _has(
          this,
          'channels[lobby].disconnections'
        )
        return hasLobbyDisconnections ? this.channels.lobby.disconnections : []
      },
      lobbyDeviceIds() {
        return _map(this.lobbyPresence, (connection) => connection.device_id)
      }
    },

    /**
     */
    watch: {
      token(token) {
        if (token) {
          const shortToken = token.slice(-8)
          if (this.debug) {
            console.log(
              `[socket] token watched. Updating socket params token (...${shortToken}).`
            )
          }
          params.token = token
        }
      }
    },

    /**
     */
    methods: {
      /**
       * Connect to the root socket transport.
       * 
       * Wraps Phoenix socket `receive` (`ok` | `error`) logic in a more 
       * idiomatic Promise that can be awaited / caught.
       */
      async connect() {
        this.connectionStart = performance.now()

        return new Promise((resolve, _reject) => {
          this.transport.connect()

          this.transport.onOpen(() => {
            this.connected = true
            this.connectionEnd = performance.now()
            const diff = Math.round(socket.connectionEnd - socket.connectionStart)

            if (this.debug) {
              console.log(`[socket] connected (${diff}ms).`)
            }

            resolve(this.transport)
          })

          this.transport.onError(() => {
            console.error('[socket] there was an error with the connection.')
          })

          this.transport.onClose(() => {
            console.warn('[socket] connection dropped.')
          })
        })
      },

      /**
       * Join a channel
       *
       * Assigned to `socket.channels[topic]`.
       * 
       * Wraps Phoenix socket `receive` (`ok` | `error`) logic in a more 
       * idiomatic Promise that can be awaited / caught.
       */
      async join(topic, params = {}) {
        if (this.debug) {
          console.log(`[socket] joining '${topic}' channel`, params)
        }

        const channel = this.transport.channel(topic, params)
        Vue.set(this.channels, topic, {
          channel,
          connections: [],
          disconnections: [],
          joins: 0
        })

        return new Promise((resolve, reject) => {
          const join = channel.join()

          join.receive('ok', (response) => {
            if (this.debug) {
            console.log(`[socket] ${topic} join response`, response)
            }

            this._onJoinSuccess(topic, channel, response)
            resolve(response)
          })

          join.receive('error', (response) => {
            this._onJoinError(topic, channel, response)
            reject(response)
          })
        })
      },

      /**
       * Internal handler for successfully joining a channel.
       *
       * Adds listeners for `presence_state` and `presence_diff`
       * which keep `socket.channels.[topic].connections` current and reactive.
       * We only do this ONCE as rejoins after disconect will re-receive the
       * join `ok`and cause duplicate / conflicting presence logic.
       */
      _onJoinSuccess(topic, channel, _response) {
        if (this.debug) {
          console.log(`[socket] joined '${topic}' channel`)
        }

        const joins = this.channels[topic].joins
        if (joins === 0) {
          channel.on('presence_state', (presenceState) => {
            this._onPresenceState(topic, presenceState)
          })

          channel.on('presence_diff', (presenceDiff) => {
            this._onPresenceDiff(topic, presenceDiff)
          })
        } else {
          console.warn(
            `[socket] Skipping presence handlers for '${topic}' (joins: ${joins})`
          )
        }
        this.channels[topic].joins++
      },

      /**
       * Internal handler for when there is an error joining a channel.
       * Errors will cause auto rejoin unless explicity aborted (leave/1).
       */
      _onJoinError(topic, _channel, { message, code }) {
        console.error(
          `[socket] channel join error. \n\t topic: '${topic}' \n\t reason: ${message}`
        )

        const leaveErrorCodes = [401]
        if (leaveErrorCodes.includes(code)) {
          console.warn(`[socket] aborting '${topic}' auto rejoin.`)
          this.leave(topic)
        }
      },

      /**
       * Leave a channel.
       *
       * Convenient since it takes a topic, and
       * does a lookup for the actual channel.
       * Also deletes the reference.
       * 
       * Wraps Phoenix socket `receive` (`ok` | `error`) logic in a more 
       * idiomatic Promise that can be awaited / caught.
       */
      async leave(topic) {
        return new Promise((resolve, reject) => {
          if (this.channels[topic]) {
            if (this.debug) {
              console.log(`[socket] leaving '${topic}' channel`)
            }

            this.channels[topic].channel
              .leave()
              .receive('ok', (response) => {
                if (this.debug) {
                  console.log(`[socket] left '${topic}' channel.`)
                }

                this.channels[topic] = null
                resolve(response)
              })
          } else {
            reject(new Error(`No channel found for '${topic}'`))
          }
        })
      },

      /**
       * Handler for `presence_state`, which is broadcast after connection.
       *
       * We won't always hear the first one since this component will likely be
       * created after the channel is joined. However, subsequent broadcasts will
       * be heard for dropped / reconnected connections; which we can use to
       * reset the connections list.
       */
      _onPresenceState(topic, presenceState) {
        this._mapAndReplacePresenceConnections(topic, presenceState)
      },

      /**
       * Update the channel connections array with the
       * metas from a `presence_state` list.
       */
      _mapAndReplacePresenceConnections(topic, presence) {
        const connections = _map(presence, (connection) => connection.metas[0])
        this.channels[topic].connections = connections
        return connections
      },

      /**
       * Handler for channel joins and leaves.
       *
       * Remove the user from the connections list for that channel.
       *
       * Note, when another user pushes a `track` message, the lobby presence
       * is updated, because presence includes `last_event`; which triggers a
       * `presence_diff` message.
       *
       * The phx_ref is unique to each iteration of presence; so we simply
       * remove the old one, and add the new one.
       *
       * Leaves will have the user's old presence data.
       * Joins will have the user's new presence data.
       *
       * Worth noting, after a dropped connection, rejoins won't have info
       * like route history until that user takes action and sends another event
       * along with the original history.
       */
      _onPresenceDiff(topic, presenceDiff) {
        // console.log(`[socket] ${topic} _onPresenceDiff`, presenceDiff)

        // Remove leaves from connections.
        // We do this first, to ensure we won't have two presences with the
        // essentially the same info (even if just briefly).
        _each(presenceDiff.leaves, (leave) => {
          leave = leave.metas[0]

          if (this.debug) {
            // console.log(`[socket] removing presence: ${topic}:${leave.id}`)
          }

          const cnxIndex = _findIndex(this.channels[topic].connections, {
            id: leave.id
          })

          // As a precaution, we don't want to start removing connections
          // if the index is -1 (not found)
          if (cnxIndex >= 0) {
            this.channels[topic].connections.splice(cnxIndex, 1)
          }
        })

        // add joins to connections
        _each(presenceDiff.joins, (join) => {
          if (this.debug) {
            console.log(
              // `[socket] adding presence: ${topic}:${join.metas[0].id}`
            )
          }
          this.channels[topic].connections.push(join.metas[0])
        })

        // If presence has `disconnected_at` prop, add leaves to disconnections.
        // When a user disconnects, we'll get (2) leave events. One for
        // when `disconnected_at` is set (which is accompanied by a join). We
        // treat this as an "update" and ignore.
        // Then another immediately after for the actual leave / close.
        // To prevent duplicates, we use the connection ID as the unique key
        // since phx_ref changes per presence update. We also only add to the
        // disconnections list if the fingerprint doesn't match one already in the
        // connections list (multiple tabs); which is actually the same user.

        _each(presenceDiff.leaves, (leave) => {
          const connections = this.channels[topic].connections
          leave = leave.metas[0]

          if (leave.disconnected_at) {
            const isUpdate =
              _find(presenceDiff.joins, ({ id }) => id === leave.id) !==
              undefined

            const isSoleConnection =
              _find(
                connections,
                ({ fingerprint }) => fingerprint === leave.fingerprint
              ) === undefined

            if (!isUpdate && isSoleConnection)
              this.channels[topic].disconnections.push(leave)
          }
        })
      },

      /**
       * Pushes a `track` message to the `lobby` channel.
       *
       * Mimics Mixpanel payload structure, but piggybacks on initial
       * socket connection params for additional data.
       *
       * It is assumed that `tracker_id` has already been set on lobby
       * presence, but it can be explicitly set (overwritten) as an
       * option (`{ tracker_id: 12345 }`).
       *
       * Also forwards tracking to `update` (Presence) with the tracking
       * set as the "last event".
       */
      track(eventName, eventProps, opts, attempt = 1) {
        if (_has(this, 'channels[lobby].channel')) {
          try {
            socket.channels.lobby.channel.push('track', {
              event: eventName,
              properties: eventProps,
              opts
            })

            // update the presence with the last tracked event
            this.update({
              last_event: { name: eventName, properties: eventProps }
            })

            // if (attempt > 1) {
            //   console.log(
            //     `[socket] lobby TRACK succeeded on attempt (${attempt}).`
            //   )
            // }
          } catch (err) {
            console.error(err)
          }
        } else if (attempt <= 5) {
          // console.warn(
          //   `[socket] not connected (attempt ${attempt}). Retrying track in 1.5s`
          // )
          setTimeout(() => {
            this.track(eventName, eventProps, opts, attempt + 1)
          }, 1500 * attempt)
        } else {
          console.error(
            '[socket] Max (5) channel push tries exceeded. Timed out waiting for lobby channel to connect.'
          )
        }
      },

      /**
       * Pushes an `update` message to the `lobby` channel.
       *
       * This updates the presence info. Unsupported keys are ignored.
       * In the event of a race condition (`update` called before successful join),
       * we attempt three retries (linear backoff).
       */
      update(data, attempt = 1) {
        if (_has(this, 'channels[lobby].channel')) {
          try {
            socket.channels.lobby.channel.push('update', { data })
            // if (attempt > 1) {
            //   console.log(
            //     `[socket] lobby update succeeded on attempt (${attempt}).`
            //   )
            // }
          } catch (err) {
            console.error(err)
          }
        } else {
          /* eslint-disable no-unused-vars */
          const p1 = `[socket] Can't send "update" message.`
          const p2 = `Not connected to lobby channel (yet?).`
          const p3 = `We'll attempt five retries before giving up.`
          // console.warn(`${p1} ${p2} ${p3}`)

          if (attempt <= 5) {
            setTimeout(() => {
              this.update(data, attempt + 1)
            }, 1500 * attempt)
          } else {
            console.error(
              'Max channel push tries exceeded. Timed out waiting for lobby channel to connect.'
            )
          }
        }
      }
    }
  })

  return socket
}
